/**
 * What the client says when Cloudflare Access has stopped believing in it.
 *
 * THE FAILURE THIS REPLACES. An Access session ends on its own schedule, and
 * from inside a loaded page the end is invisible: every `fetch` after it is
 * answered with a redirect to a login page on a different origin, which an XHR
 * cannot follow usefully. What the reader saw was the shell they already had,
 * a generic "that did not go through" per action, and a `/whoami` failure the
 * app swallowed entirely. The only way back in was to reload — which works, and
 * which nobody thinks of, because nothing on screen suggests the problem is the
 * session rather than the mail server.
 *
 * SO IT SAYS WHAT HAPPENED AND OFFERS THE ONE THING THAT FIXES IT. A reload
 * lands on the Access application, which bounces to the identity provider and
 * back, and the reader is in. That is the whole action; there is nothing to
 * configure and nothing else to try.
 *
 * IT IS NEVER AUTOMATIC. A reload the client decides on would destroy an open
 * Compose — a draft lives in component state and nowhere else — so the reader
 * presses the button when they are ready to lose whatever they are mid-way
 * through. The bar stays until they do.
 *
 * IT IS NOT A TOAST. `Toast.tsx` is deliberately not a notification centre: it
 * renders one undo and vanishes on a timer. This is the opposite kind of thing —
 * one state, permanent until acted on — and putting it there would have made
 * the undo queue into a place where important things go to disappear.
 *
 * WHY IT MOUNTS ITSELF instead of being a `<SignedOut/>` somewhere in the tree.
 * The fact it reports is discovered in `api.ts`, in the single place responses
 * are handled, and it can arrive from any call site in the client — a list
 * fetch, a settings write, a body load, the `/whoami` on boot whose failure is
 * caught and dropped. A banner that had to be wired into the shell would be
 * state threaded from `request()` up through the app and back down, and the one
 * component that forgot to pass it on would be a reader stuck with no way back.
 * Mounting from the detection point means the wiring cannot be forgotten: there
 * is exactly one caller, and removing it removes the feature rather than quietly
 * removing its reachability.
 */
import { createRoot, type Root } from 'react-dom/client'

export interface SignedOutProps {
  /** What the button does. Injected so the component itself navigates nothing. */
  readonly onSignIn: () => void
}

export function SignedOut({ onSignIn }: SignedOutProps): React.JSX.Element {
  return (
    // `alert` and not `status`: this is not progress, it is the reason nothing
    // else on screen is going to work, and a reader using a screen reader
    // should not have to go looking for it.
    <div className="signed-out" role="alert">
      <span className="signed-out-said">
        Your sign-in has expired, so the mail server is no longer answering this tab.
      </span>
      <button type="button" className="signed-out-again" onClick={onSignIn}>
        Sign in again
      </button>
    </div>
  )
}

/**
 * The node this bar lives in, and the React root rendering into it.
 *
 * HELD, AND CHECKED FOR STILL BEING IN THE DOCUMENT. Every failing request after
 * the first calls `showSignedOut` again — there is no flag in `api.ts` saying
 * "already told them", deliberately, because a flag there would be a second fact
 * about a state this module already holds — so this has to be idempotent. Asking
 * whether the host is still connected rather than merely non-null is what makes
 * it self-healing: a test that empties `document.body` between cases, or
 * anything else that removes the node, gets a fresh mount instead of a render
 * into an orphan nobody can see.
 */
let host: HTMLElement | null = null
let root: Root | null = null

/**
 * Put the bar on screen, once.
 *
 * SAFE WHERE THERE IS NO DOCUMENT. `api.ts` is imported by modules that run in
 * a plain Node test environment, and a signed-out response arriving there must
 * not throw a second error on top of the first one.
 */
export function showSignedOut(onSignIn: () => void = () => window.location.reload()): void {
  if (typeof document === 'undefined' || !document.body) return
  if (host?.isConnected && root) return

  host = document.createElement('div')
  host.className = 'signed-out-host'
  document.body.appendChild(host)
  root = createRoot(host)
  root.render(<SignedOut onSignIn={onSignIn} />)
}
